import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Calendar, FileText, Building2, Activity, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";

const ICONS = {
  exchange: Activity,
  status: Activity,
  document: FileText,
  property: Building2,
  deadline: Calendar,
  digest: Sparkles,
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const NotificationBell = ({ dark = false }) => {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/notifications")
      .then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = () => {
    api.post("/notifications/read-all").then(() => {
      setUnread(0);
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    }).catch(() => {});
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      load();
      if (unread > 0) api.post("/notifications/read-all").then(() => setUnread(0)).catch(() => {});
    }
  };

  const go = (n) => {
    setOpen(false);
    if (n.link) nav(n.link);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid="notif-bell" aria-label="Notifications"
          className={`relative ${dark ? "text-white/80" : "text-ally-slate"} hover:text-ally-pink transition-colors`}>
          <Bell size={20} />
          {unread > 0 && (
            <span data-testid="notif-badge"
              className="absolute -top-1.5 -right-1.5 bg-ally-pink text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 rounded-[24px] border border-black/5 shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <span className="font-display font-bold text-ally-ink">Notifications</span>
          <button data-testid="notif-mark-read" onClick={markAllRead} title="Mark all as read"
            className="text-ally-slate hover:text-ally-pink transition-colors flex items-center gap-1 text-xs font-bold">
            <CheckCheck size={16} /> Mark all read
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="notif-list">
          {items.length === 0 ? (
            <div className="px-5 py-12 text-center text-ally-slate text-sm">
              <Bell size={28} className="mx-auto mb-3 opacity-30" />
              You're all caught up.
            </div>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Activity;
              return (
                <button key={n.id} onClick={() => go(n)} data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left flex gap-3 px-5 py-4 hover:bg-ally-softer transition-colors border-b border-black/5 last:border-0 ${!n.read ? "bg-ally-soft/40" : ""}`}>
                  <div className="w-9 h-9 rounded-xl bg-ally-soft flex items-center justify-center text-ally-pink shrink-0">
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-ally-ink text-sm">{n.title}</div>
                    <div className="text-ally-slate text-xs mt-0.5 leading-snug">{n.body}</div>
                    <div className="text-ally-slate/60 text-[11px] mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-ally-pink mt-2 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
